import React, { useEffect } from 'react';

import {
  Box,
  Divider,
  Flex,
  Text,
  Button,
  ButtonProps,
  Icon,
} from '@chakra-ui/react';
import { IoChevronForward } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CardAddIcon } from 'src/assets/icons/card-add.svg';
import { getWallet } from 'src/hooks/queries/checkout';
import { PixIcon } from 'src/components/icons';
import { PaymentMethod, PaymentType, PaymentMethodStatus } from 'src/types/dto';
import { formatPercentage } from 'src/utils/formatters';

import { useEventData, useEventHistory } from '../hooks';
import { useCheckoutFormValuesStore } from '../stores';
import { formatCardNumber, getBrandCardIcon } from '../utils';
import { Tag } from '../_components';
import { GetWalletResponse } from 'src/types/api';
import { useQuery } from '@tanstack/react-query';
import { useEventContext } from '../provider';
import { useCheckoutStore } from '../stores';
import { useReferral } from '../hooks';
import { useInstallments } from 'src/hooks/queries/useInstallments';
import { InstallmentText } from './_components/InstallmentText';

const CheckoutButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    return (
      <Button
        display="flex"
        alignItems="center"
        colorScheme="gray"
        variant="ghost"
        w="100%"
        h="46px"
        {...props}
        {...{ ref }}
      />
    );
  },
);

const CheckoutCreditCardButton: React.FC<{ data: PaymentMethod }> = ({
  data,
}) => {
  
  const { eventID } = useParams();
  const navigate = useNavigate();
  const eventHistory = useEventHistory();
  const { setFormValues } = useCheckoutFormValuesStore();
  //const { getEventCartTotal } = useCartStore();
  const { getEventCheckout } = useCheckoutStore();
  const checkout = getEventCheckout(eventID!);
  const referral = useReferral(eventID!);
  const { maxInstallments } = useInstallments({
    sellId: checkout?.sellId,
    eventID,
    referral,
  });
  const last4Digits = formatCardNumber(data.name);
  const handleOnClickPendingCreditCard = () => {
    eventHistory.push(`/event/${eventID}/checkout/confirm-card/${data.id}`);
  };
  const handleOnClickValidCreditCard = () => {
    setFormValues({
      selectedPaymentType: PaymentType.CreditCard,
      selectedCardId: data.id,
      selectedCardName: data.name,
      selectedCardBrand: data.brand,
    });
    navigate(-1);
  };

  if (
    data.status === PaymentMethodStatus.Pending ||
    data.status === PaymentMethodStatus.WaitingPayment
  ) {
    return (
      <CheckoutButton onClick={handleOnClickPendingCreditCard}>
        <Icon w="33px" mr="4" boxSize="8" as={getBrandCardIcon(data.brand)} />
        <Box textAlign="start">
          <Text color="black" fontWeight="semibold" mb="1">
            {last4Digits}
          </Text>
          <Text fontSize="sm" color="red.500">
            Não Verificado
          </Text>
        </Box>
        <Box
          ml="auto"
          fontSize="sm"
          bg="blue.500"
          rounded="full"
          color="white"
          px="3"
          py="2">
          Verificar Cartão
        </Box>
      </CheckoutButton>
    );
  }
  // if (data.status === PaymentMethodStatus.Invalid) {
    //   return (
    //     <Flex align="center" opacity={0.4}>
    //       <Box w="40px" mr="4">
    //         <Icon
    //           color="black"
    //           boxSize="8"
    //           as={getBrandCardIcon(data.brand)}
    //           mr="4"
    //         />
    //       </Box>
    //       <Box>
    //         <Text color="black" fontWeight="semibold" mb="1">
    //           {last4Digits}
    //         </Text>
    //         <Text fontSize="sm" color="red.500">
    //           Bloqueado
    //         </Text>
    //       </Box>
    //     </Flex>
    //   );
    // }
  return (
    <CheckoutButton onClick={handleOnClickValidCreditCard}>
      <Box w="40px" mr="4">
        <Icon
          color="black"
          boxSize="8"
          as={getBrandCardIcon(data.brand)}
          mr="4"
        />
      </Box>
      <Box textAlign="start" ml="-9px">
        <Text color="black" fontWeight="semibold" mb="1">
          {last4Digits}
        </Text>
        <InstallmentText maxInstallments={maxInstallments} />
      </Box>
      <Icon color="black" ml="auto" boxSize="5" as={IoChevronForward} />
    </CheckoutButton>
  );
};

const CheckoutPaymentMethods = () => {
  const { eventID } = useParams();
  const navigate = useNavigate();
  const eventHistory = useEventHistory();
  const event = useEventData();

  const { setInvalidFieldsNames } = useEventContext();
  useEffect(() => {
    setInvalidFieldsNames?.([]);
  }, [setInvalidFieldsNames]);

  const taxCCPixDiff = event?.ticketTypes[0].taxCCPixDiff ?? 0;
  const canPayWithCreditCard = event?.paymentTypes.includes(
    PaymentType.CreditCard,
  );
  const canPayWithPix = event?.paymentTypes.includes(PaymentType.Pix);

  const { setFormValues } = useCheckoutFormValuesStore();
  const { data, isLoading } = useQuery<GetWalletResponse, Error>({
    queryKey: ['wallet'],
    queryFn: getWallet,
    enabled: !!canPayWithCreditCard,
  });

  const handleClickPix = () => {
    setFormValues({ selectedPaymentType: PaymentType.Pix });
    navigate(-1);
  };

  const handleClickAddCard = () => {
    eventHistory.push(`/event/${eventID}/checkout/add-card`);
  };
    const { formValues } = useCheckoutFormValuesStore();
    const { maxInstallments } = useInstallments({
    sellId: formValues.sellId,
    eventID: event?.id,
    referral: '', 
  });

  // TODO: adicionar skeleton
  if (isLoading) {
    return null;
  }

  return (
    <Box w="full" mx="auto" pb="8">
      {canPayWithCreditCard && (
        <>
        <CheckoutButton onClick={handleClickAddCard}>
          <Box w="33px" mr="4">
            <Icon boxSize="8" as={CardAddIcon} />
          </Box>
          <Flex direction="column" align="flex-start">
            <Text color="black" fontWeight="semibold">
              Adicionar Cartão de Crédito
            </Text>
            {maxInstallments > 1 && <InstallmentText maxInstallments={maxInstallments} />}
          </Flex>
          <Icon color="black" ml="auto" boxSize="5" as={IoChevronForward} />
        </CheckoutButton>
          <Divider border="1px" borderColor="gray.50" my="2" />
        </>
      )}
      {canPayWithPix && (
        <>
          <CheckoutButton onClick={handleClickPix}>
            <Box w="33px" mr="4">
              <PixIcon boxSize="6" color="#32bcad" />
            </Box>
            <Flex direction="column" align="flex-start">
              <Text color="black" fontWeight="semibold">
                PIX
              </Text>
              {!!taxCCPixDiff && (
                <Tag mt="1">{`${formatPercentage(
                  taxCCPixDiff,
                )} de Desconto`}</Tag>
              )}
            </Flex>
            <Icon color="black" ml="auto" boxSize="5" as={IoChevronForward} />
          </CheckoutButton>
          <Divider border="1px" borderColor="gray.50" my="2" />
        </>
      )}
      {data?.items
        ?.filter(
          creditCard =>
            creditCard.status !== PaymentMethodStatus.WaitingPayment &&
            creditCard.status !== PaymentMethodStatus.Rejected &&
            creditCard.status !== PaymentMethodStatus.Invalid &&
            creditCard.status !== PaymentMethodStatus.Removed &&
            creditCard.status !== PaymentMethodStatus.Failed,
        )
        .map(creditCard => (
          <React.Fragment key={creditCard.id}>
            <CheckoutCreditCardButton data={creditCard} />
            <Divider border="1px" borderColor="gray.50" my="2" />
          </React.Fragment>
        ))}
    </Box>
  );
};

export default CheckoutPaymentMethods;
